'use client';

import { useCallback, useMemo } from 'react';

import { useTranslations } from 'next-intl';
import { formatUnits } from 'viem';
import { useReadContract, useChainId } from 'wagmi';

import { poolContractAbi } from '@/abi';
import ArrowUpIcon from '@/assets/icons/chevron-arrow-up.svg';
import USDCIcon from '@/assets/icons/usdc.svg';
import { Image } from '@/components/shared/Image';
import { P } from '@/components/shared/Typography';
import { useTickerWebSocket } from '@/hooks/useTickerWebSocket';
import { cn } from '@/lib/utils/cn';
import { formatDecimal } from '@/lib/utils/formatDecimal';
import { C_POOL_TOKEN_DECIMALS } from '@/ts/constants/C_DECIMAL_PLACES';
import { C_ACTIVE_NETWORK_ID } from '@/ts/constants/C_NETWORKS';
import { C_USDC_DECIMALS } from '@/ts/constants/C_USDC_DECIMALS';
import { T_Pool } from '@/ts/types/T_Pool';

import type { I_MarketData } from '@/server/actions/market/getHistory.action';
import type { T_Symbol } from '@/ts/types/T_Chart';
import type { Address } from 'viem';

interface I_TickerPriceDisplayProps {
  marketData: I_MarketData;
  symbol: T_Symbol | string;
  decimals: number;
  pool?: T_Pool;
  isSticky?: boolean | undefined;
}

function TickerPriceDisplayPool({
  marketData,
  symbol,
  pool,
  isSticky
}: I_TickerPriceDisplayProps) {
  const t = useTranslations('');
  const chainId = useChainId();

  // Use real-time data if available, otherwise fall back to initial data
  const { tickerPrice } = useTickerWebSocket({
    symbol,
    enabled: true
  });
  const currentTickerPrice = tickerPrice || marketData.tickerPrice;

  // Get presaleTokenPriceDecimal and currentPriceDecimal from pool blockchain data
  const presaleTokenPriceDecimal =
    pool?.blockchainData?.presaleTokenPriceDecimal;
  const currentPriceDecimal = pool?.blockchainData?.currentPriceDecimal;

  // Check if presaleTokenPriceDecimal === currentPriceDecimal
  const shouldFetchFromContract = useMemo(() => {
    if (!presaleTokenPriceDecimal || !currentPriceDecimal) {
      return false;
    }
    return presaleTokenPriceDecimal === currentPriceDecimal;
  }, [presaleTokenPriceDecimal, currentPriceDecimal]);

  // Default to active network when wallet is not connected
  const activeChainId = chainId || C_ACTIVE_NETWORK_ID;

  // Fetch price from contract if presaleTokenPriceDecimal === currentPriceDecimal
  const { data: contractTokenPrice } = useReadContract({
    address: pool?.poolAddress as Address | undefined,
    abi: poolContractAbi,
    functionName: 'getTokenPrice',
    chainId: activeChainId,
    query: {
      enabled: shouldFetchFromContract && !!pool?.poolAddress
    }
  }) as { data: bigint | undefined };
  // Calculate display price based on the condition
  const displayPrice = useMemo(() => {
    // If presaleTokenPriceDecimal === currentPriceDecimal, use contract price
    if (shouldFetchFromContract) {
      if (contractTokenPrice !== undefined) {
        // Convert bigint to decimal string using USDC decimals (6)
        return formatUnits(contractTokenPrice, C_USDC_DECIMALS);
      }
      // If contract call is still loading or failed, fallback to currentPriceDecimal
      if (currentPriceDecimal) {
        return currentPriceDecimal;
      }
    }

    // If not equal, use currentPriceDecimal from pool blockchain data
    if (!shouldFetchFromContract && currentPriceDecimal) {
      return currentPriceDecimal;
    }

    // Fallback to marketData price
    const priceValue = parseFloat(currentTickerPrice.price);
    if (priceValue === 0 && currentPriceDecimal) {
      return currentPriceDecimal;
    }
    return currentTickerPrice.price;
  }, [
    shouldFetchFromContract,
    contractTokenPrice,
    currentTickerPrice.price,
    currentPriceDecimal
  ]);

  const formatPrice = useCallback((price: string) => {
    const numPrice = parseFloat(price);
    return formatDecimal(numPrice, C_POOL_TOKEN_DECIMALS);
  }, []);

  const formatPriceChange = useCallback((priceChange: string) => {
    const numChange = parseFloat(priceChange);
    const sign = numChange >= 0 ? '+' : '';
    return `${sign}${formatDecimal(numChange, C_POOL_TOKEN_DECIMALS)}`;
  }, []);

  const formatPriceChangePercent = useCallback((priceChangePercent: string) => {
    const numPercent = parseFloat(priceChangePercent);
    return formatDecimal(numPercent, C_POOL_TOKEN_DECIMALS);
  }, []);

  const isPositiveChange = parseFloat(currentTickerPrice.priceChange) >= 0;

  return (
    <div
      className={cn(
        'flex w-full justify-center items-center gap-1 transition-all duration-300 ease-in-out',
        isSticky ? 'px-4 py-2' : 'pt-6 pb-2.5 px-3'
      )}
    >
      <div
        className={cn(
          'w-full max-w-(--container-width) flex items-center',
          isSticky ? 'justify-center gap-6' : 'justify-between gap-4'
        )}
      >
        {!isSticky && (
          <div className="flex gap-4 items-center">
            <div className="relative w-12 h-12 ">
              <Image
                src={pool?.banner.url || ''}
                alt={pool?.banner.alt || ''}
                className=""
              />
            </div>
            <div className="flex flex-col justify-center ">
              <P className="text-xl font-bold ">
                {pool?.tokenSymbol || symbol}
              </P>
            </div>
          </div>
        )}
        <div
          className={cn(
            'flex',
            isSticky
              ? 'flex-row items-center gap-4'
              : 'flex-col items-end gap-1'
          )}
        >
          {!isSticky && (
            <P className="font-medium text-sm">{t('pools.tokenPrice')}</P>
          )}
          <P
            className="font-bold text-xl"
            icon={!isSticky ? <USDCIcon className="w-5 h-5" /> : undefined}
          >
            {formatPrice(displayPrice)}
          </P>
          <P
            icon={
              isPositiveChange ? (
                <ArrowUpIcon className="text-primary" />
              ) : (
                <ArrowUpIcon className="text-destructive transform rotate-180" />
              )
            }
            iconPosition="after"
            className={cn(
              'font-semibold',
              isSticky ? 'text-base' : 'text-sm',
              isPositiveChange ? 'text-primary' : 'text-destructive'
            )}
          >
            <span className="flex items-center justify-end gap-1 flex-wrap max-md:flex-col">
              <span>{formatPriceChange(currentTickerPrice.priceChange)}</span>
              <span>
                (
                {formatPriceChangePercent(
                  currentTickerPrice.priceChangePercent
                )}
                %)
              </span>
            </span>
          </P>
          <P className="text-muted-foreground text-sm font-semibold uppercase">
            {t('bottomMenu.live')}
          </P>
        </div>
      </div>
    </div>
  );
}

export default TickerPriceDisplayPool;
